import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DriverOvertimeActivitySummary } from '../../models/driver-activity-summary.model';
import { OvertimeManagementService } from '../../services/overtime-management.service';
import { FilterService } from '../../../../_shared/services';
import { AccountFilterData, DateIntervalFilterData, SummaryRequest, WidgetFilterData } from '../../../../_shared/models';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { Subject, Subscription } from 'rxjs';
import { DateInterval } from '../../../../_shared/enums';
import { OvertimeDriverStateService } from '../../services/overtime-driver-state.service';
import { MetricBlockComponent } from "../metric-block/metric-block.component";
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-overtime-activity-summary',
  standalone: true,
  imports: [MetricBlockComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './overtime-activity-summary.component.html',
  styleUrl: './overtime-activity-summary.component.scss'
})
export class OvertimeActivitySummaryComponent implements OnInit, OnDestroy {
  overtimeActivitySummary: DriverOvertimeActivitySummary = {} as DriverOvertimeActivitySummary;
  isLoading: boolean = true;
  accountId: number;
  dateFilter: DateIntervalFilterData;
  accountFilter: AccountFilterData;
  currentDate: Date = new Date();
  summaryRequest: SummaryRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    interval: 0,
    previousTimeFrom: subDays(startOfDay(this.currentDate), 1),
    previousTimeTo: subDays(endOfDay(this.currentDate), 1),
  };
  cancel$: Subject<void> = new Subject<void>();

  private filterSubscription: Subscription;
  private refreshSubscription: Subscription;

  intervalMap: { [key in DateInterval]: string } = {
    [DateInterval.Day]: 'overtimeManagement.intervals.yesterday',
    [DateInterval.Week]: 'overtimeManagement.intervals.lastWeek',
    [DateInterval.Month]: 'overtimeManagement.intervals.lastMonth',
    [DateInterval.Half]: 'overtimeManagement.intervals.last15Days',
    [DateInterval.Last3Hours]: 'overtimeManagement.intervals.previous3Hours',
    [DateInterval.Last6Hours]: 'overtimeManagement.intervals.previous6Hours',
    [DateInterval.Last12Hours]: 'overtimeManagement.intervals.previous12Hours',
    [DateInterval.Today]: 'overtimeManagement.intervals.yesterday',
    [DateInterval.Yesterday]: 'overtimeManagement.intervals.previousDay'
  }

  private overtimeManagementService = inject(OvertimeManagementService);
  private overtimeDriverStateService = inject(OvertimeDriverStateService);
  private filterService = inject(FilterService);
  private errorHandlerService = inject(ErrorHandlerService);

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  ngOnInit(): void {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      if (!filter || Object.values(filter).every(x => x == null)) {
        return;
      }

      this.accountFilter.accountIds = filter.accountIds;
      this.updateDateFilter(filter);
      this.onFilterChangeAsync();
    });

    this.refreshSubscription = this.overtimeDriverStateService.refreshDriverOvertime$.subscribe(() => {
      this.onFilterChangeAsync();
    });
  }

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  async onFilterChangeAsync(): Promise<void> {
    this.isLoading = true;
    this.summaryRequest.accountIds = [...this.accountFilter.accountIds];
    this.summaryRequest.interval = this.dateFilter.interval;
    this.summaryRequest.timeFrom = this.dateFilter.startDate!;
    this.summaryRequest.timeTo = this.dateFilter.endDate!;
    this.summaryRequest.previousTimeFrom = this.dateFilter.previousCycleStartDate!;
    this.summaryRequest.previousTimeTo = this.dateFilter.previousCycleEndDate!;
    await this.getActivitySummary();
  }

  async getActivitySummary() {
    try {
      this.cancel$.next();
      const result = await this.overtimeManagementService.getDriverActivitySummary(this.summaryRequest, this.cancel$);
      if (result.data) {
        this.overtimeActivitySummary = result.data;
        this.isLoading = false;
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  updateDateFilter(widgetFilter: WidgetFilterData): void {
    if (!widgetFilter) {
      return;
    }

    this.dateFilter.endDate = widgetFilter.endDate;
    this.dateFilter.startDate = widgetFilter.startDate;
    this.dateFilter.granularity = widgetFilter.granularity;
    this.dateFilter.previousCycleEndDate = widgetFilter.previousCycleEndDate;
    this.dateFilter.previousCycleStartDate = widgetFilter.previousCycleStartDate;
    this.dateFilter.interval = widgetFilter.interval;
  }
}
